import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
import { zonedTimeToUtc } from 'date-fns-tz';
import { getLineImpressionMetrics } from 'modules/api/programmaticDeal';
import { cancelFunctions } from 'utils/cancelFunctions';
import { updateLineImpressionMetrics } from 'store/dealManagement/reducer';
import { addToastNotification } from 'store/notification/reducer';
import { DealStatus, DealType } from 'components/common/types/Deal.types';
import { Store } from 'components/common/types/Store.types';
import { getCurrentLineId } from 'store/dealManagement/selectors/getCurrentLineId';
import { getCurrentLineEndDate, getCurrentLineStartDate } from 'store/dealManagement/selectors/getCurrentLineSchedule';
import { getDealType } from 'store/dealManagement/selectors/getDealType';
import { getIsNewDeal } from 'store/dealManagement/selectors/getIsNewDeal';
import { getCurrentLineBookingStatusCode } from 'store/dealManagement/selectors/getCurrentLineBookingStatusCode';
import { getSummaryAllocatedImpressions } from 'store/dealManagement/selectors/getSummaryAvailability';

export const useLineImpressionMetrics = (): void => {
  const dispatch = useDispatch();

  const isNewDeal = useSelector(getIsNewDeal);
  const dealType = useSelector(getDealType);
  const allocatedImpressions = useSelector(getSummaryAllocatedImpressions);

  const currentLineId = useSelector(getCurrentLineId);
  const currentLineStartDate = useSelector(getCurrentLineStartDate);
  const currentLineEndDate = useSelector(getCurrentLineEndDate);

  const bookingStatusCode = useSelector(getCurrentLineBookingStatusCode);
  const timezoneCode = useSelector(
    (state: Store) => state.header.timezone?.code || state.publisher.configuration.timezone,
  );

  const currentLineStatus = useMemo(
    () => !!bookingStatusCode && [DealStatus.LIVE, DealStatus.ENDED, DealStatus.TERMINATED].includes(bookingStatusCode),
    [bookingStatusCode],
  );

  const isWithLineImpressionMetrics = useMemo(
    () => !isNewDeal && !!currentLineId && allocatedImpressions !== null && dealType === DealType.GUARANTEED,
    [isNewDeal, currentLineId, allocatedImpressions, dealType],
  );

  const tryGetLineImpressionMetrics = useCallback(async (): Promise<void> => {
    try {
      const lineImpressionMetrics = await getLineImpressionMetrics(
        cancelFunctions,
        currentLineId,
        currentLineStartDate ? zonedTimeToUtc(currentLineStartDate as Date, timezoneCode).toISOString() : undefined,
        currentLineEndDate ? zonedTimeToUtc(currentLineEndDate as Date, timezoneCode).toISOString() : undefined,
      );
      dispatch(updateLineImpressionMetrics(lineImpressionMetrics));
    } catch (error) {
      if (axios.isCancel(error)) return;

      dispatch(
        addToastNotification({
          header: 'Processing your data - delivery data will show within the next hour',
          timeout: 10000,
          type: 'Info',
        }),
      );
    }
  }, [currentLineId, currentLineStartDate, currentLineEndDate, timezoneCode]);

  useEffect(() => {
    if (isWithLineImpressionMetrics && currentLineStatus) {
      tryGetLineImpressionMetrics();
    }
  }, [tryGetLineImpressionMetrics, isWithLineImpressionMetrics, currentLineStatus]);
};
